import { CONFIGS_BASE_URL } from "../../../constants";
import { getToken, isLoggedIn } from "../../../context/authContext";
import { AuthConfig } from "../../../context/config/authConfig";
import { ApiServiceManager, fetchRequest } from "../../apiServiceManager";
import { ALERTS_PATH } from "./constants";
import {
  IAlertsListResponse,
  IAlertResponse,
  ICreateAlertBody,
  IUpdateAlertBody,
  IAlertsParams,
  IAlert,
  EAlertStatus,
} from "./models";

const appProvide = async () => AuthConfig.getAppToken();
const defaultProvide = async () => await getToken();

/**
 * Get all alerts with optional filtering and pagination
 */
export const getAlerts = ({
  page = 0,
  size = 10,
  sort = "updatedAt,desc",
  searchKeyWord = "",
  status,
  severity,
  surface,
}: IAlertsParams = {}): Promise<IAlertsListResponse> => {
  let url = `${CONFIGS_BASE_URL}${ALERTS_PATH}?page=${page}&size=${size}&sort=${sort}`;

  if (searchKeyWord) {
    url += `&searchKeyWord=${encodeURIComponent(searchKeyWord)}`;
  }

  if (status) {
    url += `&status=${status}`;
  }

  if (severity) {
    url += `&severity=${severity}`;
  }

  if (surface) {
    url += `&surface=${surface}`;
  }

  const descriptor = new ApiServiceManager(
    url,
    "GET",
    isLoggedIn() ? defaultProvide : appProvide
  );

  return fetchRequest(descriptor, null);
};

/**
 * Get a specific alert by ID
 */
export const getAlertById = async (id: string): Promise<IAlert> => {
  const descriptor = new ApiServiceManager(
    `${CONFIGS_BASE_URL}${ALERTS_PATH}/${id}`,
    "GET",
    isLoggedIn() ? defaultProvide : appProvide
  );

  const response: IAlertResponse = await fetchRequest(descriptor, null);
  return response.data;
};

/**
 * Create a new alert
 */
export const createAlert = async (body: ICreateAlertBody): Promise<IAlert> => {
  const descriptor = new ApiServiceManager(
    `${CONFIGS_BASE_URL}${ALERTS_PATH}`,
    "POST",
    defaultProvide
  );

  const response: IAlertResponse = await fetchRequest(descriptor, body);
  return response.data;
};

/**
 * Update an existing alert
 */
export const updateAlert = async (
  id: string,
  body: IUpdateAlertBody
): Promise<IAlert> => {
  const descriptor = new ApiServiceManager(
    `${CONFIGS_BASE_URL}${ALERTS_PATH}/${id}`,
    "PUT",
    defaultProvide
  );

  const response: IAlertResponse = await fetchRequest(descriptor, body);
  return response.data;
};

/**
 * Delete an alert by ID
 */
export const deleteAlert = (id: string): Promise<void> => {
  const descriptor = new ApiServiceManager(
    `${CONFIGS_BASE_URL}${ALERTS_PATH}/${id}`,
    "DELETE",
    defaultProvide
  );

  return fetchRequest(descriptor, null);
};

/**
 * Toggle alert status (active/inactive)
 */
export const toggleAlertStatus = async (
  id: string,
  status: EAlertStatus
): Promise<IAlert> => {
  const descriptor = new ApiServiceManager(
    `${CONFIGS_BASE_URL}${ALERTS_PATH}/${id}/status/${status}`,
    "PATCH",
    defaultProvide
  );

  const response: IAlertResponse = await fetchRequest(descriptor, null);
  return response.data;
};
